import { Component } from '@theme/component';
import { CompareStore } from '@theme/compare-store';

/**
 * CompareButton — custom element wrapping the compare toggle on product cards.
 *
 * Attributes:
 *   data-handle         — product handle (required)
 *   data-product-title  — product title (for toast messaging)
 *
 * Listens:
 *   click              — toggles the product in CompareStore
 *   compare:updated    — syncs visual state with store
 *
 * @typedef {object} CompareButtonRefs
 * @property {HTMLButtonElement} button - The toggle button
 *
 * @extends {Component<CompareButtonRefs>}
 */
export class CompareButton extends Component {
  static observedAttributes = [];

  connectedCallback() {
    super.connectedCallback();

    this.handle = this.dataset.handle || '';
    this.productTitle = this.dataset.productTitle || '';
    this.button = this.querySelector('.compare-toggle');
    this.label = this.button?.querySelector('.compare-toggle__label');
    if (!this.button) return;

    // Bind event listeners
    this.button.addEventListener('click', this);
    window.addEventListener('compare:updated', this);
    window.addEventListener('compare:max-reached', this);

    // Set initial state
    this.updateVisualState();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this);
    window.removeEventListener('compare:updated', this);
    window.removeEventListener('compare:max-reached', this);
  }

  /**
   * Event handler for both click and custom events.
   * @param {Event} event
   */
  handleEvent(event) {
    switch (event.type) {
      case 'click':
        event.preventDefault();
        this.handleToggle();
        break;

      case 'compare:updated':
        this.updateVisualState();
        break;

      case 'compare:max-reached':
        this.showMaxReachedToast(event);
        break;
    }
  }

  /** Toggle the product in the store. */
  handleToggle() {
    const isNowSelected = CompareStore.toggle(this.handle);
    if (isNowSelected) {
      this.announce('added');
    } else {
      this.announce('removed');
    }
  }

  /** Sync button visual state with the store. */
  updateVisualState() {
    const selected = CompareStore.has(this.handle);
    this.button.classList.toggle('compare-toggle--active', selected);
    this.button.setAttribute('aria-pressed', String(selected));
    this.button.setAttribute(
      'aria-label',
      selected
        ? this.button.dataset.labelRemove || 'Remove from comparison'
        : this.button.dataset.labelAdd || 'Add to comparison'
    );
    // Update visible label text
    if (this.label) {
      this.label.textContent = selected
        ? this.button.dataset.labelRemove || 'Remove'
        : this.button.dataset.labelAdd || 'Add to compare';
    }
  }

  /**
   * Show a brief toast notification.
   * @param {'added' | 'removed'} type
   */
  announce(type) {
    // If the theme's live-region pattern is available, use it
    const live = this.closest('[aria-live]') || document.querySelector('[aria-live="assertive"]');
    if (live) {
      const msg =
        type === 'added'
          ? `${this.productTitle} added to comparison`
          : `${this.productTitle} removed from comparison`;
      live.textContent = '';
      // Force reflow so screen readers pick up the change even if same text
      requestAnimationFrame(() => {
        live.textContent = msg;
      });
    }
  }

  /**
   * Toast when user hits the 4-product limit.
   * @param {CustomEvent} event
   */
  showMaxReachedToast(event) {
    const max = event.detail?.max || 4;
    const live = this.closest('[aria-live]') || document.querySelector('[aria-live="assertive"]');
    if (live) {
      live.textContent = `Maximum ${max} products for comparison`;
    }
  }
}

if (!customElements.get('compare-button')) {
  customElements.define('compare-button', CompareButton);
}
